import axios from 'axios'
import { customAlphabet } from 'nanoid'

import type { TenantRegistration, Invites, Invite, CisrFilterStatus, ProgressFilterStatus, Tenant } from '../types/registration'
import { Paginated } from './types'

export interface FetchAllTenantRegistrationRecordsArgs {
  businessName?: string
  propertyName?: string
  tenantId?: string
  cisrStatus?: CisrFilterStatus
  progressStatus?: ProgressFilterStatus
  /** When true, the list returns archived-only records (default: active-only). */
  archivedOnly?: boolean
  page?: number
  resultsPerPage?: number
}

interface FeedRecordResponse {
  id: string
  tenantId?: string
  createdAt?: string | null
  lastUpdatedAt?: string | null
  /** ISO timestamp; null/undefined when active. */
  archivedAt?: string | null
  /** User id of the last modifier — read for archive attribution display. */
  modifiedBy?: string | null
  /** True when more than one active record shares this row's tenant.salesforceAccountId. */
  hasDuplicateSalesforceAccountId?: boolean
  hasDuplicateUtilityAccountAtProperty?: boolean
  hasDuplicateContactEmailAtProperty?: boolean
  duplicateReasons?: Array<'SALESFORCE_ACCOUNT' | 'UTILITY_ACCOUNT' | 'CONTACT_EMAIL'>
  feedData: {
    salesforceContactId?: string | null
    salesforceContactName?: string | null
    isComplete: boolean
    tenant: {
      businessName: string
      businessType: string | null
      businessEntityName?: string | null
      businessEntityType?: string | null
      stateOfFormation?: string | null
      badgeStickerType: string | null
      approximateSqft: string | null
      authorizeSpecificAccountsOnly?: boolean | null
      utilityAccountType?: 'BUSINESS' | 'INDIVIDUAL' | null
      ein?: string | null
      ssnLastFour?: string | null
      salesforceAccountId?: string | null
      salesforceAccountName?: string | null
      utilityAccounts: Array<{
        name: string
        number: string
        tenantType: 'National' | 'Local'
        billingAddress: {
          street1: string
          street2: string | null
          city: string
          state: string
          zip: string
        }
        serviceAgreements: Array<{
          serviceAgreementId: string
          meterNumber: string
          tariffSchedule: string
          salesforceProgramId?: string | null
          salesforceProgramName?: string | null
          salesforceMeterId?: string | null
          salesforceMeterName?: string | null
          serviceAddress: {
            street1: string
            street2: string | null
            city: string
            state: string
            zip: string
          }
          approximateEnergyUsage: string | null
        }>
      }>
    }
    contact: {
      title: string | null
      firstName: string
      lastName: string
      email: string
      phone: string
      department: string | null
    }
    utility: string
    property: string
    version: string
  }
  invites: Invite[]
}

/** Backend class-validator expects exactly `National` | `Local` (see UtilityAccountField). */
export function normalizeUtilityAccountTenantType(value: unknown): 'National' | 'Local' {
  const v = typeof value === 'string' ? value.trim() : ''
  const lower = v.toLowerCase()
  if (lower === 'national') return 'National'
  if (lower === 'local') return 'Local'
  return 'Local'
}

function buildInvitesState(invitesList: Invite[]): Invites {
  const onboardingRegistrationInvite = invitesList.find((invite) => invite.type === 'ONBOARDING_REGISTRATION')
  const accountSignupInvite = invitesList.find((invite) => invite.type === 'ACCOUNT_SIGNUP')

  const isOnboardingRegistrationInviteConsumed = onboardingRegistrationInvite?.consumed ?? false
  const onboardingRegistrationInviteAgreements = onboardingRegistrationInvite?.agreements ?? []

  return {
    ONBOARDING_REGISTRATION: onboardingRegistrationInvite ?? null,
    ACCOUNT_SIGNUP: accountSignupInvite ?? null,
    isOnregInviteCreated: !isOnboardingRegistrationInviteConsumed && onboardingRegistrationInviteAgreements.length === 0,
    isOnregInvitePending: !isOnboardingRegistrationInviteConsumed && onboardingRegistrationInviteAgreements.length > 0,
    // Progress flags: length checks reflect the expected signing order (TESA → TESA_SUPPLEMENT → PRICING_AND_PAYMENT_TERMS).
    // Each step is "reached" when the array length matches AND the expected type is present.
    isOnregAgreedTesa:
      onboardingRegistrationInviteAgreements.length >= 1 && onboardingRegistrationInviteAgreements.some((agreement) => agreement.type === 'TESA'),
    isOnregAgreedTesaSupplement:
      onboardingRegistrationInviteAgreements.length >= 2 && onboardingRegistrationInviteAgreements.some((agreement) => agreement.type === 'TESA_SUPPLEMENT'),
    isOnregAgreedPricingAndPayments:
      onboardingRegistrationInviteAgreements.length >= 3 &&
      onboardingRegistrationInviteAgreements.some((agreement) => agreement.type === 'PRICING_AND_PAYMENT_TERMS'),
    isOnregInviteConsumed: isOnboardingRegistrationInviteConsumed,
    isAccountInviteCreated: accountSignupInvite !== undefined,
    isAccountInviteConsumed: accountSignupInvite?.consumed === true,
  }
}

function nonEmptyStr(v: string | null | undefined): v is string {
  return v != null && String(v).trim() !== '' && String(v) !== 'null'
}

/**
 * Prefer `feedData` (synced on TESA accept; same source Registration Complete uses); fall back to
 * `prospectData.updated` when a feed field is empty (lags, invites without a tenant_feed row).
 * Third-tier fallback: `signatory_data` from the TESA agreement (covers pre-deploy v6 records where
 * the old preSave did not map signatory.businessName → tenant.businessEntityName).
 */
function coalesceFromFeedAndProspectUpdated(
  feedTenant: FeedRecordResponse['feedData']['tenant'],
  feedContact: FeedRecordResponse['feedData']['contact'] | undefined,
  onboardingInvite: Invite | undefined
): Pick<Tenant, 'type' | 'businessEntityName' | 'businessEntityType' | 'stateOfFormation' | 'utilityAccountType' | 'ein' | 'ssnLastFour'> & {
  displayName: string
  contact: Tenant['contact']
} {
  const fc = feedContact ?? ({} as NonNullable<FeedRecordResponse['feedData']['contact']>)
  const pT = onboardingInvite?.prospectUpdatedTenant
  const pC = onboardingInvite?.prospectUpdatedContact
  const sig = onboardingInvite?.signatory

  const pickStr = (feed: string | null | undefined, p: string | null | undefined): string =>
    nonEmptyStr(feed) ? String(feed).trim() : nonEmptyStr(p) ? String(p).trim() : ''
  const pickStrOrNull = (feed: string | null | undefined, p: string | null | undefined): string | null =>
    nonEmptyStr(feed) ? String(feed).trim() : nonEmptyStr(p) ? String(p).trim() : (feed ?? null)
  const pickStrOrNullWithSig = (feed: string | null | undefined, p: string | null | undefined, s: string | null | undefined): string | null =>
    nonEmptyStr(feed) ? String(feed).trim() : nonEmptyStr(p) ? String(p).trim() : nonEmptyStr(s) ? String(s!).trim() : (feed ?? null)

  const displayName = pickStr(feedTenant.businessName, pT?.businessName)

  const type =
    (nonEmptyStr(feedTenant.businessType) ? String(feedTenant.businessType).trim() : '') ||
    (nonEmptyStr(pT?.businessType) ? String(pT!.businessType).trim() : '') ||
    (nonEmptyStr(feedTenant.businessEntityType) ? String(feedTenant.businessEntityType).trim() : '') ||
    (nonEmptyStr(pT?.businessEntityType) ? String(pT!.businessEntityType).trim() : '') ||
    ''

  const utilityFromFeed = feedTenant.utilityAccountType
  const utilityFromProspect = pT?.utilityAccountType as Tenant['utilityAccountType'] | undefined

  return {
    displayName,
    type,
    businessEntityName: pickStrOrNullWithSig(feedTenant.businessEntityName ?? null, pT?.businessEntityName, sig?.businessName),
    businessEntityType: pickStrOrNullWithSig(feedTenant.businessEntityType ?? null, pT?.businessEntityType, sig?.entityType),
    stateOfFormation: pickStrOrNullWithSig(feedTenant.stateOfFormation ?? null, pT?.stateOfFormation, sig?.stateOfFormation),
    utilityAccountType: (nonEmptyStr(utilityFromFeed as string | undefined)
      ? utilityFromFeed
      : utilityFromProspect ?? utilityFromFeed) as Tenant['utilityAccountType'],
    ein:
      feedTenant.ein != null && String(feedTenant.ein).trim() !== ''
        ? feedTenant.ein
        : pT?.ein != null && pT.ein !== ''
          ? pT.ein
          : feedTenant.ein,
    ssnLastFour:
      feedTenant.ssnLastFour != null && String(feedTenant.ssnLastFour).trim() !== ''
        ? feedTenant.ssnLastFour
        : pT?.ssnLastFour != null && pT.ssnLastFour !== ''
          ? pT.ssnLastFour
          : feedTenant.ssnLastFour,
    contact: {
      title: pickStrOrNull(fc.title ?? null, pC?.title),
      firstName: pickStr(fc.firstName, pC?.firstName),
      lastName: pickStr(fc.lastName, pC?.lastName),
      emailAddress: pickStr(fc.email, pC?.email),
      phoneNumber: pickStr(fc.phone, pC?.phone),
      department: pickStrOrNull(fc.department ?? null, pC?.department),
    },
  }
}

function mapFeedRecordToRegistration(registration: FeedRecordResponse): TenantRegistration | null {
  const { feedData } = registration

  if (!feedData?.tenant) {
    console.warn(`Skipping malformed registration ${registration.id}: missing tenant field`)
    return null
  }

  const onboardingRegistrationInvite = registration.invites.find((invite) => invite.type === 'ONBOARDING_REGISTRATION')
  const invites = buildInvitesState(registration.invites)
  const coalesced = coalesceFromFeedAndProspectUpdated(feedData.tenant, feedData.contact, onboardingRegistrationInvite)

  return {
    id: registration.id,
    tenantId: registration.tenantId,
    createdAt: registration.createdAt ?? null,
    lastUpdatedAt: registration.lastUpdatedAt ?? null,
    archivedAt: registration.archivedAt ?? null,
    modifiedBy: registration.modifiedBy ?? null,
    hasDuplicateSalesforceAccountId: registration.hasDuplicateSalesforceAccountId ?? false,
    hasDuplicateUtilityAccountAtProperty: registration.hasDuplicateUtilityAccountAtProperty ?? false,
    hasDuplicateContactEmailAtProperty: registration.hasDuplicateContactEmailAtProperty ?? false,
    duplicateReasons: registration.duplicateReasons,
    salesforceContactId: feedData.salesforceContactId ?? null,
    salesforceContactName: feedData.salesforceContactName ?? null,
    isNational: feedData.tenant?.utilityAccounts?.some((ua: any) => ua.tenantType?.toLowerCase() === 'national'),
    tenant: {
      // Feed businessName (editable). Do not prefer salesforceAccountName: that field is
      // read-only enrichment, and save persists tenant.name back as businessName.
      name: coalesced.displayName,
      type: coalesced.type,
      businessEntityName: coalesced.businessEntityName,
      businessEntityType: coalesced.businessEntityType,
      stateOfFormation: coalesced.stateOfFormation,
      salesforceAccountId: feedData.tenant.salesforceAccountId ?? null,
      salesforceAccountName: feedData.tenant.salesforceAccountName ?? null,
      authorizeSpecificAccountsOnly: feedData.tenant.authorizeSpecificAccountsOnly,
      utilityAccountType: coalesced.utilityAccountType,
      ein: coalesced.ein,
      ssnLastFour: coalesced.ssnLastFour,
      contact: coalesced.contact,
      accounts: (feedData.tenant?.utilityAccounts ?? []).map((account) => ({
        name: account.name ?? '',
        number: account.number ?? '',
        tenantType: normalizeUtilityAccountTenantType(account.tenantType),
        billingAddress: {
          address: account.billingAddress?.street1 ?? '',
          address2: account.billingAddress?.street2 ?? '',
          city: account.billingAddress?.city ?? '',
          state: account.billingAddress?.state ?? '',
          zipCode: account.billingAddress?.zip ?? '',
        },
        agreements: (account.serviceAgreements ?? []).map((agreement) => ({
          number: agreement.serviceAgreementId ?? '',
          meterNumber: agreement.meterNumber ?? '',
          tariffSchedule: agreement.tariffSchedule ?? '',
          salesforceProgramId: agreement.salesforceProgramId ?? null,
          salesforceProgramName: agreement.salesforceProgramName ?? null,
          salesforceMeterId: agreement.salesforceMeterId ?? null,
          salesforceMeterName: agreement.salesforceMeterName ?? null,
          serviceAddress: {
            address: agreement.serviceAddress?.street1 ?? '',
            address2: agreement.serviceAddress?.street2 ?? '',
            city: agreement.serviceAddress?.city ?? '',
            state: agreement.serviceAddress?.state ?? '',
            zipCode: agreement.serviceAddress?.zip ?? '',
          },
          approximateEnergyUsage: agreement.approximateEnergyUsage ?? '',
        })),
      })),
      approximateSurfaceArea: feedData.tenant.approximateSqft ?? '',
      badge: feedData.tenant.badgeStickerType,
    },
    utility: feedData.utility,
    property: feedData.property,
    version: feedData.version,
    status: 'draft',
    invites,
    isComplete: !!feedData.isComplete,
  }
}

export const fetchAllTenantRegistrationRecords = async (params?: FetchAllTenantRegistrationRecordsArgs): Promise<Paginated<TenantRegistration>> => {
  const instance = axios.create()

  // Backend expects `archived='true'|'false'`. Don't send the param at all when undefined so
  // omitted callers continue to get the default (active-only) behavior.
  const { archivedOnly, ...rest } = params ?? {}
  const requestParams: Record<string, unknown> = { ...rest }
  if (archivedOnly) {
    requestParams.archived = 'true'
  }

  const response = await instance
    .request<{ results: FeedRecordResponse[]; total: number; resultsPerPage: number; page: number }>({
      method: 'GET',
      url: '/api/v1/admin/tesa/feed',
      params: requestParams,
    })
    .then((res) => res.data)

  return {
    ...response,
    results: response.results.map(mapFeedRecordToRegistration).filter((r): r is TenantRegistration => r != null),
  }
}

export const fetchTenantRegistrationRecord = async (id: string): Promise<TenantRegistration> => {
  const instance = axios.create()

  const response = await instance
    .request<FeedRecordResponse>({
      method: 'GET',
      url: `/api/v1/admin/tesa/feed/${id}`,
    })
    .then((res) => res.data)

  const result = mapFeedRecordToRegistration(response)
  if (result == null) {
    throw new Error(`Malformed registration data for ${id}`)
  }
  return result
}

export type SalesforceRecordNames = {
  programName: string | null
  meterName: string | null
  accountName: string | null
  contactName: string | null
}

/** Resolve sfdc mirror display names for the given Salesforce IDs (no save required). */
export const fetchSalesforceRecordNames = async (params: {
  programId?: string
  meterId?: string
  accountId?: string
  contactId?: string
}): Promise<SalesforceRecordNames> => {
  const search = new URLSearchParams()
  if (params.programId) search.set('programId', params.programId)
  if (params.meterId) search.set('meterId', params.meterId)
  if (params.accountId) search.set('accountId', params.accountId)
  if (params.contactId) search.set('contactId', params.contactId)

  return await axios
    .get(`/api/v1/admin/tesa/feed/salesforce-names?${search.toString()}`)
    .then((res) => res.data as SalesforceRecordNames)
}

export const previewTenantRegistrationRecords = async (file: File): Promise<TenantRegistration[]> => {
  const instance = axios.create()

  const url = '/api/v1/admin/tesa/feed/upload'

  const formData = new FormData()
  formData.set('file', file)
  formData.set('shouldPersist', '')
  // formData.set('utilityCompany', 'PG&E')

  interface ResponseData {
    registrationRecords: Array<{
      tenant: {
        businessName: string
        businessType: string | null
        badgeStickerType: string | null
        approximateSqft: string | null
        authorizeSpecificAccountsOnly?: boolean | null
        ein?: string | null
        ssnLastFour?: string | null
        utilityAccountType?: 'BUSINESS' | 'INDIVIDUAL' | null
        utilityAccounts: Array<{
          name: string
          number: string
          tenantType: 'National' | 'Local'
          billingAddress: {
            street1: string
            street2: string | null
            city: string
            state: string
            zip: string
          }
          serviceAgreements: Array<{
            serviceAgreementId: string
            meterNumber: string
            tariffSchedule: string
            serviceAddress: {
              street1: string
              street2: string | null
              city: string
              state: string
              zip: string
            }
            approximateEnergyUsage: string | null
          }>
        }>
      }
      contact: {
        title: string | null
        firstName: string
        lastName: string
        email: string
        phone: string
        department: string | null
      }
      utility: string
      property: string
      version: string
      invites: Invites
      isComplete: boolean
      isDuplicate?: boolean
      existingPropertyName?: string | null
      missingFields?: string[]
      warningFields?: string[]
    }>
  }

  const response = await instance.request<ResponseData>({
    method: 'POST',
    url,
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  const nanoid = customAlphabet('0123456789abcdef', 10)
  const registrations: TenantRegistration[] = response.data.registrationRecords.map((recordData) => {
    return {
      id: `draft-${nanoid()}`,
      tenant: {
        name: recordData.tenant.businessName,
        type: recordData.tenant.businessType,
        authorizeSpecificAccountsOnly: recordData.tenant.authorizeSpecificAccountsOnly,
        utilityAccountType: recordData.tenant.utilityAccountType,
        ein: recordData.tenant.ein,
        ssnLastFour: recordData.tenant.ssnLastFour,
        contact: {
          title: recordData.contact.title,
          firstName: recordData.contact.firstName,
          lastName: recordData.contact.lastName,
          emailAddress: recordData.contact.email,
          phoneNumber: recordData.contact.phone,
          department: recordData.contact.department,
        },
        accounts: recordData.tenant.utilityAccounts.map((accountData) => {
          return {
            name: accountData.name,
            number: accountData.number,
            tenantType: normalizeUtilityAccountTenantType(accountData.tenantType),
            billingAddress: {
              address: accountData.billingAddress.street1,
              address2: accountData.billingAddress.street2,
              city: accountData.billingAddress.city,
              state: accountData.billingAddress.state,
              zipCode: accountData.billingAddress.zip,
            },
            agreements: accountData.serviceAgreements.map((agreementData) => {
              return {
                number: agreementData.serviceAgreementId,
                meterNumber: agreementData.meterNumber,
                tariffSchedule: agreementData.tariffSchedule,
                serviceAddress: {
                  address: agreementData.serviceAddress.street1,
                  address2: agreementData.serviceAddress.street2,
                  city: agreementData.serviceAddress.city,
                  state: agreementData.serviceAddress.state,
                  zipCode: agreementData.serviceAddress.zip,
                },
                approximateEnergyUsage: agreementData.approximateEnergyUsage,
              }
            }),
          }
        }),
        approximateSurfaceArea: recordData.tenant.approximateSqft,
        badge: recordData.tenant.badgeStickerType,
      },
      utility: recordData.utility,
      property: '',
      version: '',
      status: 'draft',
      invites: recordData.invites,
      isDuplicate: recordData.isDuplicate ?? false,
      existingPropertyName: recordData.existingPropertyName ?? null,
      missingFields: recordData.missingFields ?? [],
      warningFields: recordData.warningFields ?? [],
    }
  })

  return registrations
}

export const importTenantRegistrationRecords = async (file: File, excludeBusinessNames?: string[]): Promise<TenantRegistration[]> => {
  const instance = axios.create()

  const url = '/api/v1/admin/tesa/feed/upload'

  const formData = new FormData()
  formData.set('file', file)
  formData.set('shouldPersist', 'true')
  formData.set('propertyName', 'King Property')
  if (excludeBusinessNames && excludeBusinessNames.length > 0) {
    formData.set('excludeBusinessNames', excludeBusinessNames.join('\n'))
  }

  const response = await instance.request<{ registrationRecords: TenantRegistration[] }>({
    method: 'POST',
    url,
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  return response.data.registrationRecords
}

export const updateTenantRegistrationRecord = async (
  id: string,
  data: Omit<TenantRegistration, 'id'>
): Promise<
  | {
      result: 'success'
      registration: TenantRegistration
    }
  | {
      result: 'not-found'
    }
  | {
      result: 'bad-request'
    }
> => {
  const instance = axios.create()

  const url = `/api/v1/admin/tesa/feed/${id}`

  interface FeedEditResponseData {
    id: string
    tenantId?: string | null
    archivedAt?: string | null
    modifiedBy?: string | null
    feedData: {
      isComplete: boolean
      salesforceContactId?: string | null
      tenant: FeedRecordResponse['feedData']['tenant']
      contact: {
        title: string | null
        firstName: string
        lastName: string
        email: string
        phone: string
        department: string | null
      }
      utility: string
      property: string
      version: string
    }
    invites: Invite[]
  }

  const response = await instance.request<
    FeedEditResponseData | { statusCode: 400; message: string[] } | { statusCode: 404; message: string }
  >({
    method: 'PUT',
    url,
    data: {
      id,
      tenantId: data.tenantId,
      tenant: {
        businessName: data.tenant.name,
        businessType: data.tenant.type,
        businessEntityName: data.tenant.businessEntityName,
        businessEntityType: data.tenant.businessEntityType,
        stateOfFormation: data.tenant.stateOfFormation,
        authorizeSpecificAccountsOnly: data.tenant.authorizeSpecificAccountsOnly,
        utilityAccountType: data.tenant.utilityAccountType,
        ein: data.tenant.ein,
        ssnLastFour: data.tenant.ssnLastFour,
        badgeStickerType: data.tenant.badge,
        approximateSqft: data.tenant.approximateSurfaceArea,
        utilityAccounts: data.tenant.accounts.map((accountData) => {
          return {
            name: accountData.name,
            number: accountData.number,
            tenantType: normalizeUtilityAccountTenantType(accountData.tenantType),
            billingAddress: {
              street1: accountData.billingAddress.address,
              street2: accountData.billingAddress.address2,
              city: accountData.billingAddress.city,
              state: accountData.billingAddress.state,
              zip: accountData.billingAddress.zipCode,
            },
            serviceAgreements: accountData.agreements.map((agreementData) => {
              return {
                serviceAgreementId: agreementData.number,
                meterNumber: agreementData.meterNumber,
                tariffSchedule: agreementData.tariffSchedule,
                serviceAddress: {
                  street1: agreementData.serviceAddress.address,
                  street2: agreementData.serviceAddress.address2,
                  city: agreementData.serviceAddress.city,
                  state: agreementData.serviceAddress.state,
                  zip: agreementData.serviceAddress.zipCode,
                },
                approximateEnergyUsage: agreementData.approximateEnergyUsage,
                // Admin-editable via the onboarding edit form. Empty-string convention (not null) to satisfy
                // backend @IsString DTO validation and match the other string fields on this DTO.
                salesforceProgramId: agreementData.salesforceProgramId ?? '',
                salesforceMeterId: agreementData.salesforceMeterId ?? '',
              }
            }),
          }
        }),
      },
      contact: {
        title: data.tenant.contact.title,
        firstName: data.tenant.contact.firstName,
        lastName: data.tenant.contact.lastName,
        email: data.tenant.contact.emailAddress,
        phone: data.tenant.contact.phoneNumber,
        department: data.tenant.contact.department,
      },
      utility: data.utility,
      property: data.property,
      version: data.version,
      isComplete: data.isComplete,
    },
  })

  if ('statusCode' in response.data) {
    if (response.data.statusCode === 404) {
      return { result: 'not-found' }
    } else if (response.data.statusCode === 400) {
      return { result: 'bad-request' }
    }
  }

  const editResponse = response.data as FeedEditResponseData
  const { id: _id, tenantId: _tenantId, feedData: recordData, invites: inviteList } = editResponse
  const onboardingInvite = inviteList?.find((invite) => invite.type === 'ONBOARDING_REGISTRATION')
  const coalesced = coalesceFromFeedAndProspectUpdated(recordData.tenant, recordData.contact, onboardingInvite)

  const registration: TenantRegistration = {
    id: _id,
    tenantId: _tenantId ?? undefined,
    archivedAt: editResponse.archivedAt ?? null,
    modifiedBy: editResponse.modifiedBy ?? null,
    salesforceContactId: recordData.salesforceContactId ?? null,
    isNational: recordData.tenant.utilityAccounts?.some((ua) => ua.tenantType?.toLowerCase() === 'national'),
    tenant: {
      name: coalesced.displayName,
      type: coalesced.type,
      businessEntityName: coalesced.businessEntityName,
      businessEntityType: coalesced.businessEntityType,
      stateOfFormation: coalesced.stateOfFormation,
      salesforceAccountId: recordData.tenant.salesforceAccountId ?? null,
      salesforceAccountName: recordData.tenant.salesforceAccountName ?? null,
      authorizeSpecificAccountsOnly: recordData.tenant.authorizeSpecificAccountsOnly,
      utilityAccountType: coalesced.utilityAccountType,
      ein: coalesced.ein,
      ssnLastFour: coalesced.ssnLastFour,
      contact: coalesced.contact,
      accounts: recordData.tenant.utilityAccounts.map((accountData) => {
        return {
          name: accountData.name ?? '',
          number: accountData.number ?? '',
          tenantType: normalizeUtilityAccountTenantType(accountData.tenantType),
          billingAddress: {
            address: accountData.billingAddress.street1 ?? '',
            address2: accountData.billingAddress.street2 ?? '',
            city: accountData.billingAddress.city ?? '',
            state: accountData.billingAddress.state ?? '',
            zipCode: accountData.billingAddress.zip ?? '',
          },
          agreements: accountData.serviceAgreements.map((agreementData) => {
            return {
              number: agreementData.serviceAgreementId ?? '',
              meterNumber: agreementData.meterNumber ?? '',
              tariffSchedule: agreementData.tariffSchedule ?? '',
              salesforceProgramId: agreementData.salesforceProgramId ?? null,
              salesforceMeterId: agreementData.salesforceMeterId ?? null,
              serviceAddress: {
                address: agreementData.serviceAddress.street1 ?? '',
                address2: agreementData.serviceAddress.street2 ?? '',
                city: agreementData.serviceAddress.city ?? '',
                state: agreementData.serviceAddress.state ?? '',
                zipCode: agreementData.serviceAddress.zip ?? '',
              },
              approximateEnergyUsage: agreementData.approximateEnergyUsage ?? '',
            }
          }),
        }
      }),
      approximateSurfaceArea: recordData.tenant.approximateSqft,
      badge: recordData.tenant.badgeStickerType,
    },
    utility: recordData.utility,
    property: recordData.property,
    version: recordData.version,
    status: 'draft',
    invites: buildInvitesState(inviteList ?? []),
    isComplete: !!recordData.isComplete,
  }

  return { result: 'success', registration }
}

export const markRegistrationCompleteApi = async (id: string): Promise<unknown> => {
  return await axios.put(`/api/v1/admin/tesa/feed/${id}/complete`)
}

export type OnboardingConflictType =
  | 'UTILITY_ACCOUNT_ON_OTHER_TENANT'
  | 'CONTACT_EMAIL_AT_PROPERTY'
  | 'DUPLICATE_ACTIVE_FEED'

export type CreateTenantRecommendedAction = 'MERGE' | 'CREATE' | 'NONE'

export interface OnboardingConflict {
  type: OnboardingConflictType
  utilityAccountNumber?: string
  existingTenantCuid?: string
  existingTenantAccountCuid?: string
  existingTenantAccountNumber?: string
  existingBusinessName?: string
  existingUtilityAccountName?: string
  existingPropertyName?: string
  existingFeedCuid?: string
  message: string
}

export interface CreateTenantPreview {
  feedCuid: string
  propertyName: string
  propertyExists: boolean
  utilityAccountNumbers: string[]
  conflicts: OnboardingConflict[]
  recommendedAction: CreateTenantRecommendedAction
  canMerge: boolean
  canCreate: boolean
}

/** Whether individual create must open the conflict modal instead of the simple confirm dialog. */
export function previewRequiresConflictReview(preview: CreateTenantPreview): boolean {
  return preview.recommendedAction === 'MERGE' || preview.conflicts.length > 0 || !preview.canCreate
}

export const fetchCreateTenantPreview = async (feedCuid: string): Promise<CreateTenantPreview> => {
  return await axios
    .get(`/api/v1/admin/tesa/feed/${feedCuid}/create-tenant-preview`)
    .then((res) => res.data as CreateTenantPreview)
}

export const mergeFeedIntoTenant = async (
  feedCuid: string,
  targetTenantAccountCuid: string,
): Promise<{ result: 'success'; targetTenantAccountCuid: string; metersAdded: string[] }> => {
  return await axios
    .post(`/api/v1/admin/tenant/seed/invites/${feedCuid}/merge`, { targetTenantAccountCuid })
    .then((res) => res.data as { result: 'success'; targetTenantAccountCuid: string; metersAdded: string[] })
}

/** Soft-archive a registration record. Backend re-checks the gate (no ACCOUNT_SIGNUP invite). */
export const archiveTenantRegistrationRecord = async (id: string): Promise<void> => {
  await axios.delete(`/api/v1/admin/tesa/feed/${id}`)
}

/** Restore an archived registration record. */
export const unarchiveTenantRegistrationRecord = async (id: string): Promise<void> => {
  await axios.post(`/api/v1/admin/tesa/feed/${id}/unarchive`)
}

export const exportDataApi = async (propertyName: string, type: string): Promise<string> => {
  return await axios
    .get(`/api/v1/admin/tesa/feed/invite/property/${propertyName}/links`, {
      params: { type },
      responseType: 'arraybuffer',
    })
    .then((res) => {
      return window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }))
    })
}

export const exportZipDataApi = async (propertyName: string, type: string): Promise<string> => {
  if (type !== 'TESA_EXPORT') {
    throw new Error('Invalid type: ' + type + '. Only TESA_EXPORT is supported.')
  }

  return await axios
    .get(`/api/v1/admin/tesa/feed/invite/property/${propertyName}/tesa`, {
      params: { type },
      responseType: 'blob',
    })
    .then((res) => {
      return window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }))
    })
}

export const getPropertyListApi = async (): Promise<string[]> => {
  return await axios.get(`/api/v1/admin/tesa/feed/property`).then((res) => res.data as string[])
}

export const getTESASupplementDocumentApi = async (agreementId: string, signal?: AbortSignal): Promise<string> => {
  return await axios
    .get(`/api/v1/admin/tesa/feed/agreement/${agreementId}/submission/view`, {
      responseType: 'arraybuffer',
      timeout: 30_000,
      signal,
    })
    .then((res) => window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })))
}

export const updateTESASupplementAgreementStatusApi = async (agreementId: string, action: 'ACCEPTED' | 'REJECTED'): Promise<unknown> => {
  return await axios.put(`/api/v1/admin/tesa/feed/agreement/${agreementId}/submission`, { action })
}
